import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { getEnvVariable, Loader } from '../constants/whitelabelEnv';
import { domain } from '../constants/Branding';
import LoginPage from '../components/Login';
import RegisterPage from '../components/RegisterPage';
import { RootState } from '../models/RootState';
import { SiteConfig } from '../models/SiteConfig';
import SocialMediaIcons from "../components/SocialMediaIcons";
import "./Strike46LovableLandingPage.scss";

const WhiteLabelLogo = getEnvVariable(domain, 'logo');
const BrandName = getEnvVariable(domain, 'BRAND_NAME');

interface Strike46LovableLandingPageProps {
  referalCode?: string;
  siteConfig?: SiteConfig;
}

// Reusable icon components
const FlameIcon = ({ className = "w-5 h-5 text-[hsl(275,90%,64%)]" }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"></path>
  </svg>
);

const HeartIcon = ({ className = "w-4 h-4", textColor }: { className?: string; textColor?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} style={{ color: textColor || 'var(--text-white)' }}>
    <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path>
  </svg>
);

const ChevronLeftIcon = ({ className = "w-4 h-4", textColor }: { className?: string; textColor?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} style={{ color: textColor }}>
    <path d="m15 18-6-6 6-6"></path>
  </svg>
);

const ChevronRightIcon = ({ className = "w-4 h-4", textColor }: { className?: string; textColor?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} style={{ color: textColor }}>
    <path d="m9 18 6-6-6-6"></path>
  </svg>
);

const ZapIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} style={{ color: 'var(--icon-yellow)' }}>
    <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"></path>
  </svg>
);

const RocketIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} style={{ color: 'var(--icon-orange)' }}>
    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"></path>
    <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"></path>
    <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"></path>
    <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"></path>
  </svg>
);

const Strike46LovableLandingPage: React.FC<Strike46LovableLandingPageProps> = ({ referalCode, siteConfig }) => {
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [bannerSlide, setBannerSlide] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Helper function to calculate brightness from hex color
  const getColorBrightness = (color: string): number => {
    if (!color) return 0;
    
    // Remove # if present
    const hex = color.replace('#', '');
    
    // Handle 8-digit hex (with alpha) - use first 6 digits for RGB
    const rgbHex = hex.length === 8 ? hex.substring(0, 6) : hex.length === 6 ? hex : hex.substring(0, 6);
    
    // Convert hex to RGB
    const r = parseInt(rgbHex.substring(0, 2), 16);
    const g = parseInt(rgbHex.substring(2, 4), 16);
    const b = parseInt(rgbHex.substring(4, 6), 16);
    
    // Calculate brightness using relative luminance formula
    return (r * 299 + g * 587 + b * 114) / 1000;
  };

  // Helper function to get text color based on background color
  const getTextColorForBackground = (bgColor: string): string => {
    if (!bgColor) return '#FFFFFF';
    const brightness = getColorBrightness(bgColor);
    return brightness > 128 ? '#000000' : '#FFFFFF';
  };

  // Helper function to determine if header theme is light or dark based on primary color (header background)
  const isHeaderLightTheme = () => {
    const primaryColor = siteConfig?.colorConfig?.primary;
    if (!primaryColor) return false;
    return getColorBrightness(primaryColor) > 128;
  };

  // Helper function to determine if footer theme is light or dark based on primary color (footer background)
  const isFooterLightTheme = () => {
    const primaryColor = siteConfig?.colorConfig?.primary;
    if (!primaryColor) return false;
    return getColorBrightness(primaryColor) > 128;
  };

  // Helper function to determine if main background is light or dark
  const isMainBgLightTheme = () => {
    const backgroundColor = siteConfig?.colorConfig?.background;
    if (!backgroundColor) return false;
    return getColorBrightness(backgroundColor) > 128;
  };

  // Theme colors for header icons and buttons
  // For dark header: white icons/text, no filter
  // For light header: black icons/text, no filter
  const headerThemeIconColor = isHeaderLightTheme() ? '#000000' : '#FFFFFF';
  const headerThemeTextColor = isHeaderLightTheme() ? '#000000' : '#FFFFFF';
  const headerThemeFilter = 'none'; // No inversion needed - we're setting the color directly

  // Theme colors for footer
  const footerTextColor = isFooterLightTheme() ? '#000000' : '#FFFFFF';

  // Theme colors for main page background
  const mainPageTextColor = isMainBgLightTheme() ? '#000000' : '#FFFFFF';

  // Theme colors for balance pill background
  const balancePillTextColor = getTextColorForBackground(siteConfig?.colorConfig?.primary || '#2A1F3A');

  // Theme colors for modal close button background
  const modalCloseTextColor = getTextColorForBackground('#FFFFFF1A'); // var(--modal-close-bg) default

  // Theme colors for section header arrow buttons
  const sectionArrowTextColor = getTextColorForBackground('#4A3A5A'); // var(--purple-button-bg) default

  // Theme colors for favorite button background
  const favoriteButtonTextColor = getTextColorForBackground('#0000004D'); // var(--favorite-btn-bg) default

  // Theme colors for login button background (purple-accent which is primaryTeamInfo)
  const loginButtonTextColor = getTextColorForBackground(siteConfig?.colorConfig?.primaryTeamInfo || '#8B5CF6');

  // Refs for scrollable sections
  const themesRef = useRef<HTMLDivElement>(null);
  const recentGamesRef = useRef<HTMLDivElement>(null);
  const liveCasinoRef = useRef<HTMLDivElement>(null);
  const slotsRef = useRef<HTMLDivElement>(null);
  const crashGamesRef = useRef<HTMLDivElement>(null);

  const openLogin = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    setShowLogin(true);
  };
  const closeLogin = () => setShowLogin(false);

  const openRegister = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    setShowRegister(true);
  };
  const closeRegister = () => setShowRegister(false);

  // Scroll function for carousels
  const scrollContainer = (ref: React.RefObject<HTMLDivElement>, direction: 'left' | 'right') => {
    if (ref.current) {
      const scrollAmount = 300; // pixels to scroll
      const newScrollPosition = direction === 'left' 
        ? ref.current.scrollLeft - scrollAmount 
        : ref.current.scrollLeft + scrollAmount;
      
      ref.current.scrollTo({
        left: newScrollPosition,
        behavior: 'smooth'
      });
    }
  };

  // Auto-rotate banner carousel on mobile
  useEffect(() => {
    const interval = setInterval(() => {
      setBannerSlide((prev) => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Initial check for siteConfig on mount
  useEffect(() => {
    if (!siteConfig) {
      setIsLoading(true);
    }
  }, []);

  // Apply colorConfig colors to CSS variables
  useEffect(() => {
    const root = document.documentElement;
    let colorsApplied = false;
    
    // Apply primary to --header-bg with alpha channel (95% opacity = F2)
    if (siteConfig?.colorConfig?.primary) {
      const primaryColor = siteConfig.colorConfig.primary;
      let headerBgColor = primaryColor;
      if (primaryColor.startsWith('#')) {
        // If it's a 6-digit hex, add alpha channel (F2 = 95% opacity)
        if (primaryColor.length === 7) {
          headerBgColor = primaryColor + 'F2';
        }
      }
      root.style.setProperty('--header-bg', headerBgColor);
      colorsApplied = true;
    }
    
    // Apply background to --purple-dark-bg with alpha channel (95% opacity = F2)
    if (siteConfig?.colorConfig?.background) {
      const backgroundColor = siteConfig.colorConfig.background;
      let darkBgColor = backgroundColor;
      if (backgroundColor.startsWith('#')) {
        // If it's a 6-digit hex, add alpha channel (F2 = 95% opacity)
        if (backgroundColor.length === 7) {
          darkBgColor = backgroundColor + 'F2';
        }
      }
      root.style.setProperty('--purple-dark-bg', darkBgColor);
      colorsApplied = true;
    }
    
    // Apply primaryTeamInfo to --purple-accent
    if (siteConfig?.colorConfig?.primaryTeamInfo) {
      root.style.setProperty('--purple-accent', siteConfig.colorConfig.primaryTeamInfo);
      // Also set it as register button hover background
      root.style.setProperty('--register-button-hover-bg', siteConfig.colorConfig.primaryTeamInfo);
      colorsApplied = true;
    }
    
    // Apply primary to footer background with alpha channel (75% opacity = BF for start, 80% opacity = CC for end)
    if (siteConfig?.colorConfig?.primary) {
      const primaryColor = siteConfig.colorConfig.primary;
      let footerBgStart = primaryColor;
      let footerBgEnd = primaryColor;
      if (primaryColor.startsWith('#')) {
        // If it's a 6-digit hex, add alpha channels
        if (primaryColor.length === 7) {
          footerBgStart = primaryColor + 'BF'; // 75% opacity
          footerBgEnd = primaryColor + 'CC';   // 80% opacity
        }
      }
      root.style.setProperty('--footer-bg-start', footerBgStart);
      root.style.setProperty('--footer-bg-end', footerBgEnd);
      colorsApplied = true;
    }
    
    // Hide loader once colors are applied or if siteConfig is loaded (even without colorConfig)
    // Add a delay to ensure CSS variables are applied and rendered, and to show loader for a reasonable duration
    if (colorsApplied || (siteConfig !== undefined)) {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        setTimeout(() => {
          setIsLoading(false);
        }, 1500); // Extended delay to show loader for 1.5 seconds
      });
    } else {
      // If siteConfig is not loaded yet, keep loader visible
      setIsLoading(true);
    }
  }, [siteConfig?.colorConfig?.primary, siteConfig?.colorConfig?.background, siteConfig?.colorConfig?.primaryTeamInfo, siteConfig]);

  // Promotional banners with CDN images
  const promoBanners = [
    { id: 1, image: 'https://stage-cdn.stwin2025.co/TG/BotYaPot/Banners/Images/r01.jpg', alt: 'Promo Banner 1' },
    { id: 2, image: 'https://stage-cdn.stwin2025.co/TG/BotYaPot/Banners/Images/r02.png', alt: 'Promo Banner 2' },
    { id: 3, image: 'https://stage-cdn.stwin2025.co/TG/BotYaPot/Banners/Images/6854404469-Dpgw.webp', alt: 'Promo Banner 3' },
  ];

  // Theme cards with CDN images
  const themes = [
    { name: 'Lotus', image: 'https://cdn.stwin2025.co/TG/BotYaPot/skins/lotus.png' },
    { name: 'Diamond', image: 'https://cdn.stwin2025.co/TG/BotYaPot/skins/diamond.png' },
    { name: 'Ultra46', image: 'https://cdn.stwin2025.co/TG/BotYaPot/skins/ultra.png' },
    { name: 'Laser', image: 'https://cdn.stwin2025.co/TG/BotYaPot/skins/laser.png' },
    { name: 'Tiger', image: 'https://cdn.stwin2025.co/TG/BotYaPot/skins/tiger.png' },
  ];

  // Game data with CDN images
  const recentGames = [
    { name: 'Mines', image: 'https://cdn.dreamdelhi.com/crash/mines.webp' },
    { name: 'Hi Lo', image: 'https://cdn.dreamdelhi.com/crash88/hilo.webp' },
    { name: 'Aviator', image: 'https://cdn.dreamdelhi.com/strike/aviator.webp' },
    { name: 'Diamonds', image: 'https://cdn.dreamdelhi.com/crash/diamonds.webp' },
    { name: 'Space X', image: 'https://cdn.dreamdelhi.com/crash88/spacex.webp' },
    { name: 'Candy Bonanza1000', image: 'https://cdn.dreamdelhi.com/crashslots/candybonanza1000.webp' },
    { name: 'Plinko', image: 'https://cdn.dreamdelhi.com/crash/plinko.webp' },
  ];

  const liveCasinoGames = [
    { name: 'Bet on Poker', image: 'https://cdn.dreamdelhi.com/crashslots/sugarfrenzy1000.webp' },
    { name: '5 Card Poker', image: 'https://cdn.dreamdelhi.com/crashslots/megaace.webp' },
    { name: 'Rogues Draw', image: 'https://cdn.dreamdelhi.com/crashslots/mahjongmastery2.webp' },
    { name: 'Blackjack Surrender', image: 'https://cdn.dreamdelhi.com/crashslots/candybonanza1000.webp' },
    { name: 'Fire Blaze Sisters', image: 'https://cdn.dreamdelhi.com/crash/limbo.webp' },
    { name: 'Money Coming', image: 'https://cdn.dreamdelhi.com/crash88/balloonx.webp' },
    { name: 'Charge Buffalo', image: 'https://cdn.dreamdelhi.com/crash/plinko.webp' },
  ];

  const slotGames = [
    { name: 'Aviator', image: 'https://cdn.dreamdelhi.com/updated/jili_fortune_gems.webp' },
    { name: 'Plinko', image: 'https://cdn.dreamdelhi.com/updated/jili_golden_empire.webp' },
    { name: 'Space X', image: 'https://cdn.dreamdelhi.com/updated/jili_romax.webp' },
    { name: 'Diamonds', image: 'https://cdn.dreamdelhi.com/updated/jili_pharaoh_treasure.webp' },
    { name: 'Hi Lo', image: 'https://cdn.dreamdelhi.com/updated/jili_super_rich.webp' },
    { name: 'Mines', image: 'https://cdn.dreamdelhi.com/updated/jili_boxing_king.webp' },
    { name: 'Candy Bonanza', image: 'https://cdn.dreamdelhi.com/crashslots/candybonanza1000.webp' },
  ];

  const crashGames = [
    { name: 'Aviator', image: 'https://cdn.dreamdelhi.com/strike/aviator.webp' },
    { name: 'Space X', image: 'https://cdn.dreamdelhi.com/crash88/spacex.webp' },
    { name: 'Plinko', image: 'https://cdn.dreamdelhi.com/crash/plinko.webp' },
    { name: 'Mines', image: 'https://cdn.dreamdelhi.com/crash/mines.webp' },
    { name: 'Hi Lo', image: 'https://cdn.dreamdelhi.com/crash88/hilo.webp' },
    { name: 'Diamonds', image: 'https://cdn.dreamdelhi.com/crash/diamonds.webp' },
    { name: 'Candy Bonanza', image: 'https://cdn.dreamdelhi.com/crashslots/candybonanza1000.webp' },
  ];

  // Game card component with image support
  const GameCard = ({ name, image, onClick }: { name: string; image: string; onClick?: () => void }) => (
    <div 
      onClick={onClick || openLogin}
      className="game-tile cursor-pointer flex-shrink-0 w-36 md:w-48"
    >
      <button className="absolute top-1.5 right-1.5 p-1.5 rounded-full transition-colors z-10" style={{ backgroundColor: 'var(--favorite-btn-bg)' }} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--favorite-btn-hover)'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--favorite-btn-bg)'; }}>
        <HeartIcon textColor={favoriteButtonTextColor} />
      </button>
      <img src={image} alt={name} />
    </div>
  );

  // Theme card component with image
  const ThemeCard = ({ name, image }: { name: string; image: string }) => (
    <div 
      onClick={openLogin}
      className="game-tile cursor-pointer"
    >
      <button className="absolute top-1.5 right-1.5 p-1.5 rounded-full transition-colors z-10" style={{ backgroundColor: 'var(--favorite-btn-bg)' }} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--favorite-btn-hover)'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--favorite-btn-bg)'; }}>
        <HeartIcon textColor={favoriteButtonTextColor} />
      </button>
      <img src={image} alt={name} />
    </div>
  );

  // Section header component with scroll functionality
  const SectionHeader = ({ 
    icon, 
    title, 
    showArrows = true,
    onScrollLeft,
    onScrollRight
  }: { 
    icon: React.ReactNode; 
    title: string; 
    showArrows?: boolean;
    onScrollLeft?: () => void;
    onScrollRight?: () => void;
  }) => (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        {icon}
        <h2 className="text-[1.25rem] md:text-[1.375rem] font-bold" style={{ color: mainPageTextColor }}>{title}</h2>
      </div>
      {showArrows && (
        <div className="flex gap-1">
          <button 
            onClick={onScrollLeft}
            className="p-1.5 rounded-full transition-colors"
            style={{ backgroundColor: 'var(--purple-button-bg)' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--purple-button-hover)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--purple-button-bg)'; }}
          >
            <ChevronLeftIcon textColor={sectionArrowTextColor} />
          </button>
          <button 
            onClick={onScrollRight}
            className="p-1.5 rounded-full transition-colors"
            style={{ backgroundColor: 'var(--purple-button-bg)' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--purple-button-hover)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--purple-button-bg)'; }}
          >
            <ChevronRightIcon textColor={sectionArrowTextColor} />
          </button>
        </div>
      )}
    </div>
  );

  // WhiteLabelLoader component using whitelabel loader image
  const WhiteLabelLoader = () => {
    const loaderBgColor = siteConfig?.colorConfig?.background || 'var(--purple-dark-bg)';
    const loaderSrc = Loader(domain);
    
    return (
      <div 
        className="fixed inset-0 z-[200] flex items-center justify-center"
        style={{ 
          backgroundColor: loaderBgColor,
          transition: 'opacity 0.3s ease-out'
        }}
      >
        <div className="home-loading">
          <img 
            src={loaderSrc} 
            style={{ height: '10rem' }} 
            alt="Loading..." 
          />
        </div>
      </div>
    );
  };

  return (
    <>
      {isLoading && <WhiteLabelLoader />}
      <div 
        className="min-h-screen landing-page overflow-hidden" 
        style={{ 
          backgroundColor: 'var(--purple-dark-bg)', 
          color: mainPageTextColor,
          opacity: isLoading ? 0 : 1,
          transition: 'opacity 0.3s ease-in'
        }}
      >
        {/* Animated background elements */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 -left-32 w-96 h-96 rounded-full blur-3xl animate-pulse" style={{ backgroundColor: 'var(--glow-amber)' }} />
          <div className="absolute bottom-1/4 -right-32 w-96 h-96 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s', backgroundColor: 'var(--glow-violet)' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full blur-3xl" style={{ backgroundColor: 'var(--glow-emerald)' }} />
        </div>

        {/* Header */}
        <header className="landing-header" style={{ borderBottomColor: 'var(--header-border)' }}>
          <div className="container mx-auto flex h-16 md:h-20 items-center justify-between gap-2 md:gap-4 px-3 md:px-6">
            {/* Logo and Support Icon */}
            <div className="flex items-center gap-2 shrink-0">
              <a href="#" onClick={(e) => e.preventDefault()} className="flex items-center">
                <img src={WhiteLabelLogo} alt={BrandName} className="h-4 md:h-10 w-auto max-w-[60px] md:max-w-none" />
              </a>
              {/* Support Icon */}
              <button className="p-1 md:p-2 rounded-full transition-colors flex" style={{ backgroundColor: 'transparent' }} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--overlay-white-10)'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 md:w-6 md:h-6" style={{ color: headerThemeIconColor, filter: headerThemeFilter }}>
                  <path d="M3 14h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a9 9 0 0 1 18 0v7a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3"></path>
                </svg>
              </button>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1 md:gap-4 shrink-0">
              {/* Notification Bell */}
              <button className="p-1 md:p-2.5 rounded-full transition-colors relative" style={{ backgroundColor: 'transparent' }} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--overlay-white-10)'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 md:w-6 md:h-6" style={{ color: headerThemeIconColor, filter: headerThemeFilter }}>
                  <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"></path>
                  <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"></path>
                </svg>
              </button>

              {/* Desktop Login Button */}
              <button
                onClick={openLogin}
                className="hidden sm:inline-flex items-center justify-center whitespace-nowrap text-base font-medium transition-colors h-10 rounded-lg px-4 gap-2 shadow-lg"
                style={{ backgroundColor: 'var(--purple-accent)', color: loginButtonTextColor }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" style={{ color: loginButtonTextColor }}>
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
                  <polyline points="10 17 15 12 10 7"></polyline>
                  <line x1="15" x2="3" y1="12" y2="12"></line>
                </svg>
                Login
              </button>

              {/* Desktop Register Button */}
              <button
                onClick={openRegister}
                className="hidden sm:inline-flex items-center justify-center whitespace-nowrap text-base font-medium transition-all duration-200 border-2 bg-transparent h-10 rounded-lg px-4 gap-2"
                style={{ borderColor: 'var(--purple-accent)', color: headerThemeTextColor }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" style={{ color: headerThemeTextColor }}>
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <line x1="19" x2="19" y1="8" y2="14"></line>
                  <line x1="22" x2="16" y1="11" y2="11"></line>
                </svg>
                Register
              </button>

              {/* Mobile Login Button */}
              <a href="#" onClick={(e) => { e.preventDefault(); openLogin(); }} className="sm:hidden">
                <button
                  className="inline-flex items-center justify-center whitespace-nowrap font-medium transition-colors rounded-md h-7 px-2 text-xs gap-1 shadow-lg"
                  style={{ backgroundColor: 'var(--purple-accent)', color: loginButtonTextColor }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3" style={{ color: loginButtonTextColor }}>
                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
                    <polyline points="10 17 15 12 10 7"></polyline>
                    <line x1="15" x2="3" y1="12" y2="12"></line>
                  </svg>
                  Login
                </button>
              </a>

              {/* Mobile Register Button */}
              <button
                onClick={openRegister}
                className="sm:hidden inline-flex items-center justify-center whitespace-nowrap font-medium transition-all duration-200 border bg-transparent rounded-md h-7 px-2 text-xs gap-1"
                style={{ borderColor: 'var(--purple-accent)', color: headerThemeTextColor }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3" style={{ color: headerThemeTextColor }}>
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                  <circle cx="9" cy="7" r="4"></circle>
                  <line x1="19" x2="19" y1="8" y2="14"></line>
                  <line x1="22" x2="16" y1="11" y2="11"></line>
                </svg>
                Register
              </button>
            </div>
          </div>
        </header>

        {/* Top padding for fixed header */}
        <div className="h-16 md:h-20"></div>

        {/* Main Content */}
        <main className="relative z-10" style={{ backgroundColor: 'var(--purple-dark-bg)', color: mainPageTextColor }}>
          {/* Promotional Banners Section */}
          <section className="py-4">
            <div className="container mx-auto px-4">
              {/* Desktop: 3-column grid */}
              <div className="hidden md:grid md:grid-cols-3 gap-4">
                {promoBanners.map((banner) => (
                  <div
                    key={banner.id}
                    onClick={openLogin}
                    className="relative overflow-hidden rounded-xl cursor-pointer transition-transform hover:scale-105"
                  >
                    <img 
                      src={banner.image} 
                      alt={banner.alt} 
                      className="w-full h-auto object-cover rounded-xl"
                    />
                  </div>
                ))}
              </div>

              {/* Mobile: Carousel */}
              <div className="md:hidden relative">
                <div className="overflow-hidden rounded-xl">
                  <div 
                    className="flex transition-transform duration-500 ease-out"
                    style={{ transform: `translateX(-${bannerSlide * 100}%)` }}
                  >
                    {promoBanners.map((banner) => (
                      <div 
                        key={banner.id}
                        onClick={openLogin}
                        className="w-full flex-shrink-0 cursor-pointer"
                      >
                        <img 
                          src={banner.image} 
                          alt={banner.alt} 
                          className="w-full h-auto object-cover rounded-xl"
                        />
                      </div>
                    ))}
                  </div>
                </div>
                {/* Carousel indicators */}
                <div className="flex justify-center gap-2 mt-3">
                  {promoBanners.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setBannerSlide(index)}
                      className={`h-2 rounded-full transition-all ${
                        bannerSlide === index ? 'w-4' : 'w-2'
                      }`}
                      style={{ backgroundColor: bannerSlide === index ? 'var(--purple-accent)' : 'var(--carousel-indicator-inactive)' }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Lucky Theme Section - Only show if isSitesEnabled is true */}
          {siteConfig?.isSitesEnabled && (
            <section className="pt-4 pb-2">
              <div className="container mx-auto px-4">
                <SectionHeader 
                  icon={<FlameIcon />} 
                  title="Play on your lucky Theme" 
                  showArrows={true}
                  onScrollLeft={() => scrollContainer(themesRef, 'left')}
                  onScrollRight={() => scrollContainer(themesRef, 'right')}
                />

                {/* Mobile: Horizontal scroll */}
                <div 
                  ref={themesRef}
                  className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4 md:hidden"
                >
                  {themes.map((theme) => (
                    <div key={theme.name} className="flex-shrink-0 w-32">
                      <ThemeCard {...theme} />
                    </div>
                  ))}
                </div>

                {/* Desktop: 6-column grid */}
                <div className="hidden md:grid md:grid-cols-6 gap-4">
                  {themes.map((theme) => (
                    <ThemeCard key={theme.name} {...theme} />
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Recent Games Section */}
          <section className="pt-4 pb-20">
            <div className="container mx-auto px-4">
              <SectionHeader 
                icon={<FlameIcon />} 
                title="Recent Games"
                onScrollLeft={() => scrollContainer(recentGamesRef, 'left')}
                onScrollRight={() => scrollContainer(recentGamesRef, 'right')}
              />
              <div 
                ref={recentGamesRef}
                className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4"
              >
                {recentGames.map((game, index) => (
                  <GameCard key={index} {...game} />
                ))}
              </div>
            </div>
          </section>

          {/* Live Casino Section */}
          <section className="py-4">
            <div className="container mx-auto px-4">
              <SectionHeader 
                icon={<FlameIcon />} 
                title="Live Casino"
                onScrollLeft={() => scrollContainer(liveCasinoRef, 'left')}
                onScrollRight={() => scrollContainer(liveCasinoRef, 'right')}
              />
              <div 
                ref={liveCasinoRef}
                className="flex gap-3 overflow-x-auto pb-4 scrollbar-hide -mx-4 px-4"
              >
                {liveCasinoGames.map((game, index) => (
                  <GameCard key={index} {...game} />
                ))}
              </div>
            </div>
          </section>

          {/* Slots Section */}
          <section className="py-4">
            <div className="container mx-auto px-4">
              <SectionHeader 
                icon={<ZapIcon />} 
                title="Slots"
                onScrollLeft={() => scrollContainer(slotsRef, 'left')}
                onScrollRight={() => scrollContainer(slotsRef, 'right')}
              />
              <div 
                ref={slotsRef}
                className="flex gap-3 overflow-x-auto pb-4 scrollbar-hide -mx-4 px-4"
              >
                {slotGames.map((game, index) => (
                  <GameCard key={index} {...game} />
                ))}
              </div>
            </div>
          </section>

          {/* Crash Games Section */}
          <section className="py-4 pb-24">
            <div className="container mx-auto px-4">
              <SectionHeader 
                icon={<RocketIcon />} 
                title="Crash Games"
                onScrollLeft={() => scrollContainer(crashGamesRef, 'left')}
                onScrollRight={() => scrollContainer(crashGamesRef, 'right')}
              />
              <div 
                ref={crashGamesRef}
                className="flex gap-3 overflow-x-auto pb-4 scrollbar-hide -mx-4 px-4"
              >
                {crashGames.map((game, index) => (
                  <GameCard key={index} {...game} />
                ))}
              </div>
            </div>
          </section>
        </main>

        {/* Floating Social Media Sidebar - Dynamic from siteConfig */}
        <SocialMediaIcons siteConfig={siteConfig} />

        {/* Bottom Navigation */}
        <nav className="bottom-nav backdrop-blur" style={{ 
          background: `linear-gradient(180deg, ${siteConfig?.colorConfig?.primary || 'var(--footer-bg-start)'} 0%, ${siteConfig?.colorConfig?.primary || 'var(--footer-bg-end)'} 100%)`,
          borderTopColor: 'var(--bottom-nav-border)' 
        }}>
          <div className="container mx-auto">
            <div className="flex items-center justify-around py-3">
              {/* Casino */}
              <button onClick={openLogin} className="bottom-nav-item" style={{ color: footerTextColor }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="nav-icon" style={{ color: footerTextColor }}>
                  <path d="M5 9c-1.5 1.5-3 3.2-3 5.5A5.5 5.5 0 0 0 7.5 20c1.8 0 3-.5 4.5-2 1.5 1.5 2.7 2 4.5 2a5.5 5.5 0 0 0 5.5-5.5c0-2.3-1.5-4-3-5.5l-7-7-7 7Z"></path>
                  <path d="M12 18v4"></path>
                </svg>
                <span className="nav-label">Casino</span>
              </button>

              {/* Sports */}
              <button onClick={openLogin} className="bottom-nav-item" style={{ color: footerTextColor }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="nav-icon" style={{ color: footerTextColor }}>
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M19.13 5.09C15.22 9.14 10 10.44 2.25 10.94"></path>
                  <path d="M21.75 12.84c-6.62-1.41-12.14 1-16.38 6.32"></path>
                  <path d="M8.56 2.75c4.37 6 6 9.42 8 17.72"></path>
                </svg>
                <span className="nav-label">Sports</span>
              </button>

              {/* Home */}
              <button onClick={openLogin} className="bottom-nav-item" style={{ color: footerTextColor }}>
                <img src={WhiteLabelLogo} alt={BrandName} className="nav-logo" />
                <span className="nav-label">Home</span>
              </button>

              {/* Promos */}
              <button onClick={openLogin} className="bottom-nav-item active" style={{ color: footerTextColor }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="nav-icon" style={{ color: footerTextColor }}>
                  <path d="m3 11 18-5v12L3 14v-3z"></path>
                  <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"></path>
                </svg>
                <span className="nav-label">Promos</span>
              </button>

              {/* Wallet */}
              <button onClick={openLogin} className="bottom-nav-item" style={{ color: footerTextColor }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="nav-icon" style={{ color: footerTextColor }}>
                  <path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1"></path>
                  <path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4"></path>
                </svg>
                <span className="nav-label">Wallet</span>
              </button>
            </div>
          </div>
        </nav>

        {/* Bottom padding for fixed nav */}
        <div className="h-20"></div>
      </div>

      {/* Login Modal */}
      {showLogin && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="absolute inset-0 backdrop-blur-sm" onClick={closeLogin} style={{ backgroundColor: 'var(--modal-bg)' }} />
          <div className="relative z-10 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <button
              onClick={closeLogin}
              className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full flex items-center justify-center transition-colors"
              style={{ backgroundColor: 'var(--modal-close-bg)' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--modal-close-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--modal-close-bg)'; }}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: modalCloseTextColor }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <LoginPage 
              referalCode={referalCode} 
              theme="lovable" 
              onRegisterClick={() => { closeLogin(); openRegister(); }}
              backgroundColor={siteConfig?.colorConfig?.background}
              inputBackgroundBox={siteConfig?.colorConfig?.inputBackgroundBox}
              tableBorder={siteConfig?.colorConfig?.tableBorder}
              primaryColor={siteConfig?.colorConfig?.primary}
              backgroundTeamInfo={siteConfig?.colorConfig?.backgroundTeamInfo}
            />
          </div>
        </div>
      )}

      {/* Register Modal */}
      {showRegister && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="absolute inset-0 backdrop-blur-sm" onClick={closeRegister} style={{ backgroundColor: 'var(--modal-bg)' }} />
          <div className="relative z-10 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <button
              onClick={closeRegister}
              className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full flex items-center justify-center transition-colors"
              style={{ backgroundColor: 'var(--modal-close-bg)' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--modal-close-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--modal-close-bg)'; }}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: modalCloseTextColor }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <RegisterPage 
              theme="lovable" 
              onSwitchToLogin={() => { closeRegister(); openLogin(); }}
              backgroundColor={siteConfig?.colorConfig?.background}
              inputBackgroundBox={siteConfig?.colorConfig?.inputBackgroundBox}
              tableBorder={siteConfig?.colorConfig?.tableBorder}
              primaryColor={siteConfig?.colorConfig?.primary}
              backgroundTeamInfo={siteConfig?.colorConfig?.backgroundTeamInfo}
            />
          </div>
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    siteConfig: state.userDetails?.siteConfig,
  };
};

export default connect(mapStateToProps)(Strike46LovableLandingPage);
